import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../routing/LangRouter";
import { toast } from "react-toastify";

//import utils
import { numberWithCommas } from "../../utils/utility";

//import customized hooks
import useTotalPrice from "../../hooks/useTotalPrice";
import useCaptcha from "../../hooks/useCaptcha";
import useDomesticBooking from "../../hooks/useDomesticBooking";
import useInternationalBooking from "../../hooks/useInternationalBooking";
import useRevalidate from "../../hooks/useRevalidate";

//import general components
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import QuestionModal from "../../components/common/question-modal/question-modal";

//import view components
import InternationalInformation from "../../components/view/confirm-information/international-information";
import DomesticInformation from "../../components/view/confirm-information/domestic-information";
import PayInformation from "../../components/view/confirm-information/pay-information";

//import styles
import "./confirm-information.scss";
import useBookingPassengers from "../../hooks/useBookingPassengers";
import { IS_MOBILE } from "../../constants/breakpoints";
import ExpirationResultModal from "../../components/common/expiration-result-modal/expiration-result-modal";

function Confirmation() {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    //sessionStorage variables
    const { isHotelInternational, isFlightInternational } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));

    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight"));
    const flightPassengers = JSON.parse(sessionStorage.getItem("flightPassengers"));

    const selectedIntFlight = JSON.parse(sessionStorage.getItem("selectedIntFlight"));
    const intFlightPassengers = JSON.parse(sessionStorage.getItem("intFlightPassengers"));

    const busInformation = JSON.parse(sessionStorage.getItem("busInformation"));
    const selectedBus = JSON.parse(sessionStorage.getItem("selectedBus"));
    const busPassengers = JSON.parse(sessionStorage.getItem("busPassengers"));
    const uidData = JSON.parse(sessionStorage.getItem("uidData"));

    const selectedHotel = JSON.parse(sessionStorage.getItem("selectedHotel"));
    const hotelPassengers = JSON.parse(sessionStorage.getItem("hotelPassengers")) || JSON.parse(sessionStorage.getItem("intHotelPassengers"));
    const selectedRooms = JSON.parse(sessionStorage.getItem("selectedRooms")) || JSON.parse(sessionStorage.getItem("selectedRooms"));

    const isInternational = isHotelInternational || isFlightInternational;
    const isTwoWay = domesticFlightInformation?.returnDate;

    //helper function to determine the navigation path
    const determineNavigationPath = () => {
        if (selectedHotel && selectedRooms) return `/${locale}/hotel-passengers`;
        if (selectedFlight) return `/${locale}/flight-passengers`;
        if (selectedIntFlight) return `/${locale}/int-flight-passengers`;
        if (selectedRooms) return `/${locale}/int-hotel-passengers`;
        if (selectedBus) return `/${locale}/bus-passengers`;
        return "/";
    };

    useEffect(() => {
        if (!selectedRooms && !selectedFlight && !selectedIntFlight && !busInformation && !busPassengers) {
            goToHome();
        }
    }, []);

    //navigate to the determined path
    const editInformation = () => {
        const navigationPath = determineNavigationPath();
        navigate(navigationPath, { replace: true });
    };

    //calculate total Price
    const [newTotalPrice, setNewTotalPrice] = useState();
    const [newFlightPrice, setNewFlightPrice] = useState(0);
    const totalPrice = useTotalPrice(
        selectedFlight,
        selectedIntFlight,
        domesticFlightInformation,
        selectedHotel,
        selectedRooms,
        hotelPassengers,
        selectedBus,
        busPassengers,
    );

    // State to track if payment is by credit
    const [isPaymentByCredit, setIsPaymentByCredit] = useState(false);

    // Hook for revalidating flight prices and state for tracking new prices
    const { revalidateBooking } = useRevalidate(isFlightInternational, isHotelInternational, setLoading);

    // Revalidates price for a single flight
    const revalidatePrice = async (data, type, errorRedirect) => {
        if (!data || data.tag !== "pr") return 0;

        const fareSourceCode = data.fare_source_code;
        const totalPrice =
            type === "intFlight" || (type === "domesticFlight" && !!isTwoWay)
                ? data.fares?.total_price
                : type === "prHotel"
                  ? data.fare?.total
                  : type === "grsHotel"
                    ? data.prices?.total_price
                    : data.total_price;

        const newPrice = await revalidateBooking(fareSourceCode, errorRedirect);

        if (newPrice !== totalPrice) {
            if (type === "domesticFlight") {
                toast.info(t("changePriceMessage"), { toastId: "price-change-toast" });
                setNewFlightPrice(newPrice);
            }
            return newPrice;
        } else return 0;
    };

    // Revalidates all selected flights and updates total price
    const revalidateAll = async () => {
        // const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

        const results = await Promise.all([
            revalidatePrice(selectedFlight, "domesticFlight", "domestic-flight"),
            revalidatePrice(selectedIntFlight, "intFlight", "international-flight"),
            revalidatePrice(selectedRooms, "prHotel", "hotel"),
            revalidatePrice(selectedRooms, "grsHotel", "hotel"),
        ]);

        const shouldBookInternational = (isFlightInternational || isHotelInternational) && (results?.[1] === 0 || results?.[2] === 0);
        const shouldBookDomestic = !isInternational && results?.[0] === 0 && results?.[2] === 0 && results?.[3] === 0;
        if (shouldBookInternational) {
            internationalBooking();
        } else if (shouldBookDomestic) {
            domesticBooking();
        }
        const totalPrice = results.reduce((sum, price) => sum + (price || 0), 0);
        setNewTotalPrice(totalPrice);
    };
    //change the passenger list to the required format
    const { updatedFlightPassengers, updatedHotelPassengers } = useBookingPassengers(
        isHotelInternational,
        isFlightInternational,
        selectedIntFlight,
        flightPassengers,
        intFlightPassengers,
        hotelInformation,
        selectedHotel,
        selectedRooms,
        hotelPassengers,
    );

    //////////////Domestic//////////////
    //request get captcha
    const { captcha, getCaptcha } = useCaptcha(selectedFlight);
    useEffect(() => {
        getCaptcha();
    }, []);

    //retry the request get captcha
    const refreshCaptcha = () => {
        getCaptcha();
    };

    //input value for captcha
    const [captchaValue, setCaptchaValue] = useState("");

    //update the captcha value based on the departure and return filght
    const [captchaSlice, setCaptchaSlice] = useState({ departure: null, return: null });
    useEffect(() => {
        const updateCaptcha = () => {
            if (!selectedFlight?.return_segments?.length && selectedFlight?.tag === "zm") {
                const departureCaptchaSlice = captchaValue?.slice(0, 4);
                setCaptchaSlice(values => ({ ...values, departure: departureCaptchaSlice }));
            }

            if (!!selectedFlight?.return_segments?.length && selectedFlight?.tag === "zm") {
                const returnCaptchaSlice = selectedFlight?.tag === "zm" ? captchaValue?.slice(4, 8) : captchaValue?.slice(0, 4);
                setCaptchaSlice(values => ({ ...values, return: returnCaptchaSlice }));
            }
        };

        updateCaptcha();
    }, [captchaValue]);

    //request for domestic flight and hotel booking
    const { openModalPriceChange, setOpenModalPriceChange, domesticBooking, ordersPayment } = useDomesticBooking(
        domesticFlightInformation,
        selectedFlight,
        selectedHotel,
        hotelInformation,
        selectedBus,
        busPassengers,
        uidData,
        captcha,
        captchaSlice,
        updatedFlightPassengers,
        updatedHotelPassengers,
        totalPrice,
        isPaymentByCredit,
        getCaptcha,
        setLoading,
        setNewTotalPrice,
        isTwoWay,
    );

    //handle close modal
    const goToHome = () => {
        navigate(`/${locale}`);
    };

    //////////////International//////////////

    //request for domestic flight and hotel booking
    const { internationalBooking } = useInternationalBooking(
        selectedIntFlight,
        updatedFlightPassengers,
        updatedHotelPassengers,
        isPaymentByCredit,
        setLoading,
        hotelInformation,
        selectedHotel,
        updatedHotelPassengers,
        isHotelInternational,
    );

    const navigateToBusPage = () => {
        sessionStorage.removeItem("uidData");
        sessionStorage.removeItem("selectedBus");
        sessionStorage.removeItem("busPassengers");
        navigate(`/${locale}/bus`);
    };
    return (
        <Grid
            className="confirm-information"
            container
            item
            xs={12}
            mt={IS_MOBILE ? 6 : 5}
            mb={{ xs: selectedFlight?.tag === "zm" ? 30 : 20, md: 5 }}
        >
            <MobileHeader title={t("confirmInformation")} justifyContent="center" handleClick={editInformation} />
            <Container maxWidth="xl">
                <Grid container item xs={12} rowGap={{ xs: 0, md: 5 }}>
                    {isFlightInternational || isHotelInternational ? (
                        <InternationalInformation />
                    ) : (
                        <DomesticInformation newFlightPrice={newFlightPrice} />
                    )}

                    <PayInformation
                        loading={loading}
                        captcha={captcha}
                        totalPrice={totalPrice}
                        newTotalPrice={newTotalPrice}
                        captchaValue={captchaValue}
                        setCaptchaValue={setCaptchaValue}
                        refreshCaptcha={refreshCaptcha}
                        booking={revalidateAll}
                        editInformation={editInformation}
                        isPaymentByCredit={isPaymentByCredit}
                        setIsPaymentByCredit={setIsPaymentByCredit}
                    />
                </Grid>
            </Container>

            <QuestionModal
                open={openModalPriceChange}
                setOpen={setOpenModalPriceChange}
                question={t("Price.Price Confirmation")}
                colorYes="blue-gray"
                colorNo="teal-18"
                handleClick={ordersPayment}
                close={goToHome}
            >
                <div className="confirm-information__description">
                    {t("Messages.Supplier Price Update")}{" "}
                    <Grid container columnGap={1} justifyContent="center" my={1}>
                        {t("from")} <span className="confirm-information__old-price"> {numberWithCommas(totalPrice)} ریال</span>
                    </Grid>
                    <Grid container columnGap={1} justifyContent="center" my={1}>
                        {t("to")} <span className="confirm-information__new-price"> {numberWithCommas(newTotalPrice)} ریال</span>
                    </Grid>
                </div>
            </QuestionModal>
            <ExpirationResultModal requestExpirationDate={uidData?.requestExpirationDate} uid={uidData?.uid} resetList={() => navigateToBusPage()} />
        </Grid>
    );
}

export default Confirmation;
